import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Clock, 
  FolderOpen, 
  Target, 
  TrendingUp,
  Calendar,
  Activity
} from 'lucide-react';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import 'chart.js/auto';

const COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', 
  '#8B5CF6', '#06B6D4', '#84CC16', '#F97316',
  '#EC4899', '#6366F1', '#14B8A6', '#F43F5E'
];

const TimeOverviewGraph = ({ period = 'week' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('projects');
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTimeOverview();
  }, [period]);

  const fetchTimeOverview = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`/api/analytics/time-overview?period=${period}`);
      setData(response.data);
    } catch (err) {
      console.error('Failed to fetch time overview:', err);
      setError('Failed to load time overview data');
    } finally {
      setLoading(false);
    }
  };

  const renderProjectChart = () => {
    if (!data?.projectBreakdown?.length) {
      return (
        <div className="flex items-center justify-center h-64 text-secondary-500 dark:text-secondary-400">
          <div className="text-center">
            <FolderOpen className="h-12 w-12 mx-auto mb-2 text-secondary-400" />
            <p>No project data for this period</p>
            <p className="text-sm mt-1">Start logging time to see your project breakdown</p>
          </div>
        </div>
      );
    }

    const chartData = {
      labels: data.projectBreakdown.map(item => item.name),
      datasets: [{
        label: 'Hours',
        data: data.projectBreakdown.map(item => item.hours),
        backgroundColor: COLORS[0],
        borderColor: COLORS[0],
        borderWidth: 1,
        borderRadius: 4,
      }]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              const item = data.projectBreakdown[context.dataIndex];
              return `${item.name}: ${item.hours}h (${item.percentage}%)`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Hours'
          }
        },
        x: {
          ticks: {
            maxRotation: 45,
            minRotation: 0,
            maxTicksLimit: 6,
            autoSkip: true,
            font: { size: typeof window !== 'undefined' && window.innerWidth < 640 ? 10 : 12 },
          }
        }
      }
    };

    return <Bar data={chartData} options={options} />;
  };

  const renderTaskChart = () => {
    if (!data?.taskBreakdown?.length) {
      return (
        <div className="flex items-center justify-center h-64 text-secondary-500 dark:text-secondary-400">
          <div className="text-center">
            <Target className="h-12 w-12 mx-auto mb-2 text-secondary-400" />
            <p>No task data for this period</p>
            <p className="text-sm mt-1">Log time on tasks to see your breakdown</p>
          </div>
        </div>
      );
    }

    const chartData = {
      labels: data.taskBreakdown.map(item => item.name),
      datasets: [{
        label: 'Hours',
        data: data.taskBreakdown.map(item => item.hours),
        backgroundColor: COLORS[1],
        borderColor: COLORS[1],
        borderWidth: 1,
        borderRadius: 4,
      }]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              const item = data.taskBreakdown[context.dataIndex];
              return `${item.name}: ${item.hours}h (${item.percentage}%)`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Hours'
          }
        },
        x: {
          ticks: {
            maxRotation: 45,
            minRotation: 0,
            maxTicksLimit: 6,
            autoSkip: true,
            font: { size: typeof window !== 'undefined' && window.innerWidth < 640 ? 10 : 12 },
          }
        }
      }
    };

    return <Bar data={chartData} options={options} />;
  };

  const renderCategoryChart = () => {
    if (!data?.categoryBreakdown?.length) {
      return (
        <div className="flex items-center justify-center h-64 text-secondary-500 dark:text-secondary-400">
          <div className="text-center">
            <Activity className="h-12 w-12 mx-auto mb-2 text-secondary-400" />
            <p>No category data for this period</p>
          </div>
        </div>
      );
    }

    const chartData = {
      labels: data.categoryBreakdown.map(item => item.name),
      datasets: [{
        data: data.categoryBreakdown.map(item => item.hours),
        backgroundColor: COLORS.slice(0, data.categoryBreakdown.length),
        borderWidth: 2,
        borderColor: '#fff'
      }]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 20
          }
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              const item = data.categoryBreakdown[context.dataIndex];
              return `${item.name}: ${item.hours}h (${item.percentage}%)`;
            }
          }
        }
      }
    };

    return <Doughnut data={chartData} options={options} />;
  };

  const renderDailyChart = () => {
    if (!data?.dailyBreakdown?.length) {
      return (
        <div className="flex items-center justify-center h-64 text-secondary-500 dark:text-secondary-400">
          <div className="text-center">
            <Calendar className="h-12 w-12 mx-auto mb-2 text-secondary-400" />
            <p>No daily data for this period</p>
            <p className="text-sm mt-1">Log time to see your daily trend</p>
          </div>
        </div>
      );
    }

    const chartData = {
      labels: data.dailyBreakdown.map(item => 
        new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      ),
      datasets: [{
        label: 'Hours',
        data: data.dailyBreakdown.map(item => item.hours),
        borderColor: COLORS[2],
        backgroundColor: COLORS[2] + '20',
        borderWidth: 3,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: COLORS[2],
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6
      }]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            title: function(context) {
              const date = new Date(data.dailyBreakdown[context[0].dataIndex].date);
              return date.toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              });
            },
            label: function(context) {
              return `Hours: ${context.parsed.y}h`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Hours'
          }
        },
        x: {
          title: {
            display: typeof window === 'undefined' || window.innerWidth >= 640,
            text: 'Date'
          },
          ticks: {
            maxRotation: 45,
            maxTicksLimit: 7,
            autoSkip: true,
            font: { size: typeof window !== 'undefined' && window.innerWidth < 640 ? 10 : 12 },
          }
        }
      }
    };

    return <Line data={chartData} options={options} />;
  };

  const tabs = [
    { id: 'projects', label: 'By Project', icon: FolderOpen },
    { id: 'tasks', label: 'By Task', icon: Target },
    { id: 'categories', label: 'By Category', icon: Activity },
    { id: 'daily', label: 'Daily Trend', icon: Calendar }
  ];

  const renderChart = () => {
    switch (activeTab) {
      case 'projects':
        return renderProjectChart();
      case 'tasks':
        return renderTaskChart();
      case 'categories':
        return renderCategoryChart();
      case 'daily':
        return renderDailyChart();
      default:
        return renderProjectChart();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 text-red-600">
        <div className="text-center">
          <p className="font-medium">{error}</p>
          <button 
            onClick={fetchTimeOverview}
            className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-primary-600 dark:bg-primary-500 text-white'
                : 'bg-secondary-100 dark:bg-secondary-700 text-secondary-700 dark:text-secondary-300 hover:bg-secondary-200 dark:hover:bg-secondary-600'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Chart Content */}
      <div className="h-56 sm:h-72 md:h-80">
        {renderChart()}
      </div>
    </div>
  );
};

export default TimeOverviewGraph;
